import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  App, Button, Card, Dropdown, Empty, Space, Spin, Tag, Typography,
} from 'antd';
import { deleteDeal, getDeals, getPipeline, moveDeal } from 'api/pipelines';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { PERMISSIONS } from 'constants/permissions';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { getData } from 'utils';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';

import DealFormDrawer from './DealFormDrawer';
import EditStagesDrawer from './EditStagesDrawer';
import { closedStageTagLabel, formatDealCount } from './utils';
import './pipeline-board.css';

const COLUMN_SCROLL_STEP = 296;
const UNASSIGNED_STAGE_KEY = '__unassigned__';
const DEAL_MENU_EDIT = '__deal_edit__';
const DEAL_MENU_DELETE = '__deal_delete__';

const { Text, Title } = Typography;

function ClosedStageBadge({ stage }) {
  const label = closedStageTagLabel(stage);
  if (!label) return null;
  const variant = stage.is_won ? 'won' : 'lost';
  return (
    <span className={`pipeline-closed-badge pipeline-closed-badge--${variant}`}>
      {label}
    </span>
  );
}

function buildStageMoveMenuItems(stages, deal) {
  const openStages = (stages ?? []).filter((s) => !s.is_won && !s.is_lost);
  const closeStages = (stages ?? []).filter((s) => s.is_won || s.is_lost);

  const stageItem = (stage) => ({
    key: stage.stage_key,
    label: (
      <Space size={4} style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space size={4}>
          {stage.name}
          <ClosedStageBadge stage={stage} />
        </Space>
        {stage.stage_key === deal.stage_key && <CheckOutlined style={{ color: '#1677ff' }} />}
      </Space>
    ),
    disabled: stage.stage_key === deal.stage_key,
  });

  const items = [
    {
      type: 'group',
      label: 'Move to stage',
      children: openStages.map(stageItem),
    },
  ];
  if (closeStages.length > 0) {
    items.push({
      type: 'group',
      label: 'Close deal',
      children: closeStages.map(stageItem),
    });
  }
  return items;
}

function buildDealCardMenuItems(stages, deal) {
  const moveItems = buildStageMoveMenuItems(stages, deal);
  return [
    {
      key: DEAL_MENU_EDIT,
      label: 'Edit deal',
      icon: <EditOutlined />,
    },
    { type: 'divider' },
    ...moveItems,
    { type: 'divider' },
    {
      key: DEAL_MENU_DELETE,
      label: 'Delete deal',
      icon: <DeleteOutlined />,
      danger: true,
    },
  ];
}

function resolveDropStageKey(over, deals, stageKeys) {
  if (!over) return null;
  const overId = String(over.id);
  if (stageKeys.includes(overId)) return overId;
  if (overId.startsWith('deal-')) {
    const dealId = Number(overId.replace('deal-', ''));
    const deal = deals.find((d) => d.id === dealId);
    return deal?.stage_key ?? null;
  }
  return null;
}

function formatValue(deal) {
  if (deal.value == null || deal.value === '') return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: deal.currency || 'USD',
  }).format(Number(deal.value));
}

function contactName(customer) {
  if (!customer) return '—';
  return customer.name || customer.email || '—';
}

function DealCardContent({
  deal,
  onClick,
  onEdit,
  onDelete,
  stages,
  canMoveStage,
  onMoveStage,
  deleteLoading,
}) {
  const valueLabel = formatValue(deal);
  const menuItems = buildDealCardMenuItems(stages, deal);

  const handleMenuClick = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === DEAL_MENU_EDIT) {
      onEdit(deal);
      return;
    }
    if (key === DEAL_MENU_DELETE) {
      onDelete(deal);
      return;
    }
    onMoveStage(deal.id, key);
  };

  return (
    <Card
      size="small"
      hoverable
      onClick={onClick}
      style={{ marginBottom: 8, cursor: 'pointer' }}
      bodyStyle={{ padding: 12 }}
    >
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 4, marginBottom: 4,
      }}
      >
        <Text
          strong
          ellipsis={{ tooltip: deal.title }}
          style={{ flex: 1, minWidth: 0 }}
        >
          {deal.title}
        </Text>
        {canMoveStage && (stages?.length ?? 0) > 0 && (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: menuItems,
              onClick: handleMenuClick,
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              loading={deleteLoading}
              aria-label="Deal actions"
              style={{ flexShrink: 0, margin: '-4px -8px 0 0' }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            />
          </Dropdown>
        )}
      </div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
        {contactName(deal.customer)}
      </Text>
      {deal.company?.name && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          {deal.company.name}
        </Text>
      )}
      {valueLabel && (
        <Text style={{ display: 'block', marginTop: 4 }}>{valueLabel}</Text>
      )}
    </Card>
  );
}

function DraggableDealCard({
  deal,
  canDrag,
  canMoveStage,
  onEdit,
  onDelete,
  stages,
  onMoveStage,
  deleteLoading,
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `deal-${deal.id}`,
    data: { deal },
    disabled: !canDrag,
  });

  return (
    <div
      ref={setNodeRef}
      {...(canDrag ? { ...listeners, ...attributes } : {})}
      style={{ opacity: isDragging ? 0.4 : 1, touchAction: canDrag ? 'none' : 'auto' }}
    >
      <DealCardContent
        deal={deal}
        stages={stages}
        canMoveStage={canMoveStage}
        onMoveStage={onMoveStage}
        deleteLoading={deleteLoading}
        onClick={() => onEdit(deal)}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}

function StageColumn({
  stage,
  deals,
  canDrag,
  canMoveStage,
  onEditDeal,
  onDeleteDeal,
  stages,
  onMoveStage,
  deletingDealId,
}) {
  const droppableId = stage.stage_key === UNASSIGNED_STAGE_KEY ? null : stage.stage_key;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId ?? `noop-${stage.stage_key}`,
    disabled: !droppableId,
  });
  const columnClass = [
    'pipeline-column',
    isOver ? 'pipeline-column--drop-target' : '',
  ].filter(Boolean).join(' ');
  const headerClass = [
    'pipeline-column__header',
    stage.is_won ? 'pipeline-column__header--won' : '',
    stage.is_lost ? 'pipeline-column__header--lost' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={setNodeRef} className={columnClass}>
      <div className={headerClass}>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space wrap align="center" size={8}>
            <Title level={5} style={{ margin: 0 }}>{stage.name}</Title>
            <ClosedStageBadge stage={stage} />
            {stage.stage_key === UNASSIGNED_STAGE_KEY && (
              <Tag color="warning">Unknown stage</Tag>
            )}
          </Space>
          <Text type="secondary">
            {formatDealCount(stage.deal_count ?? deals.length)}
          </Text>
          {deals.length > 0 && (stage.has_more || (stage.deal_count ?? 0) > deals.length) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Showing
              {' '}
              {deals.length}
              {' '}
              of
              {' '}
              {stage.deal_count}
              {' '}
              (most recently updated)
            </Text>
          )}
        </Space>
      </div>
      <div className="pipeline-column__body">
        {deals.map((deal) => (
          <DraggableDealCard
            key={deal.id}
            deal={deal}
            canDrag={canDrag}
            canMoveStage={canMoveStage}
            onEdit={onEditDeal}
            onDelete={onDeleteDeal}
            stages={stages}
            onMoveStage={onMoveStage}
            deleteLoading={deletingDealId === deal.id}
          />
        ))}
      </div>
    </div>
  );
}

function PipelineBoardScroll({ stageCount, children, freezeScrollChrome }) {
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  const [canScroll, setCanScroll] = useState(false);
  const frozenRef = useRef(false);
  frozenRef.current = Boolean(freezeScrollChrome);

  const updateScrollState = useCallback(() => {
    if (frozenRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const overflow = scrollWidth > clientWidth + 4;
    setCanScroll(overflow);
    setEdges({
      left: overflow && scrollLeft > 8,
      right: overflow && scrollLeft + clientWidth < scrollWidth - 8,
    });
  }, []);

  useEffect(() => {
    if (!freezeScrollChrome) {
      updateScrollState();
    }
  }, [freezeScrollChrome, updateScrollState, stageCount, children]);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(() => {
      if (!frozenRef.current) updateScrollState();
    });
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState, stageCount, children]);

  const scrollByStep = (direction) => {
    scrollerRef.current?.scrollBy({ left: direction * COLUMN_SCROLL_STEP, behavior: 'smooth' });
  };

  return (
    <div className="pipeline-board-shell">
    <div className="pipeline-board">
      {canScroll && (
        <div className="pipeline-board-meta">
          <Text type="secondary" className="pipeline-board-scroll-hint">
            {stageCount}
            {' '}
            stages — scroll horizontally or use the arrows to see all columns
          </Text>
        </div>
      )}
      {edges.left && <div className="pipeline-board-fade pipeline-board-fade--left" aria-hidden />}
      {edges.right && <div className="pipeline-board-fade pipeline-board-fade--right" aria-hidden />}
      {edges.left && (
        <Button
          type="default"
          shape="circle"
          size="small"
          className="pipeline-board-nav pipeline-board-nav--left"
          icon={<LeftOutlined />}
          aria-label="Scroll pipeline left"
          onClick={() => scrollByStep(-1)}
        />
      )}
      {edges.right && (
        <Button
          type="default"
          shape="circle"
          size="small"
          className="pipeline-board-nav pipeline-board-nav--right"
          icon={<RightOutlined />}
          aria-label="Scroll pipeline right"
          onClick={() => scrollByStep(1)}
        />
      )}
      <div ref={scrollerRef} className="pipeline-board-scroll">
        {children}
      </div>
    </div>
    </div>
  );
}

function Pipelines() {
  const { message, modal } = App.useApp();
  const hasCrmAccess = useHasAnyOfPermissions([PERMISSIONS.CUSTOMERS]);
  const canEditStages = useHasAnyOfPermissions([PERMISSIONS.ADMIN_ACCESS]);
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [activeDeal, setActiveDeal] = useState(null);
  const [stagesDrawerOpen, setStagesDrawerOpen] = useState(false);
  const [dealDrawer, setDealDrawer] = useState({ open: false, deal: null, stageKey: null });
  const [deletingDealId, setDeletingDealId] = useState(null);
  const [orphanMeta, setOrphanMeta] = useState({ count: 0, hasMore: false });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const loadBoard = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const [pipelineRes, dealsRes] = await Promise.all([getPipeline(), getDeals()]);
      const pipelineData = getData(pipelineRes)?.data ?? getData(pipelineRes);
      const dealsList = getData(dealsRes)?.data ?? getData(dealsRes) ?? [];
      setStages(pipelineData?.stages ?? []);
      setOrphanMeta({
        count: pipelineData?.orphan_deal_count ?? 0,
        hasMore: pipelineData?.orphan_has_more ?? false,
      });
      setDeals(Array.isArray(dealsList) ? dealsList : []);
    } catch (err) {
      captureException(err);
      message.error(getHttpErrorMessage(err));
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const { dealsByStage, unassignedDeals } = useMemo(() => {
    const map = {};
    stages.forEach((s) => { map[s.stage_key] = []; });
    const unassigned = [];
    deals.forEach((d) => {
      if (map[d.stage_key]) map[d.stage_key].push(d);
      else unassigned.push(d);
    });
    const sortDeals = (list) => list.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    Object.keys(map).forEach((key) => { sortDeals(map[key]); });
    sortDeals(unassigned);
    return { dealsByStage: map, unassignedDeals: unassigned };
  }, [deals, stages]);

  const boardStages = useMemo(() => {
    if (orphanMeta.count <= 0 && unassignedDeals.length === 0) return stages;
    return [
      ...stages,
      {
        stage_key: UNASSIGNED_STAGE_KEY,
        name: 'Unassigned',
        deal_count: orphanMeta.count || unassignedDeals.length,
        has_more: orphanMeta.hasMore,
        is_won: false,
        is_lost: false,
      },
    ];
  }, [stages, orphanMeta, unassignedDeals.length]);

  const handleDragStart = ({ active }) => {
    const dealId = Number(String(active.id).replace('deal-', ''));
    setActiveDeal(deals.find((d) => d.id === dealId) ?? null);
  };

  const handleMoveDeal = useCallback(async (dealId, targetStageKey) => {
    if (!hasCrmAccess) return;
    if (targetStageKey === UNASSIGNED_STAGE_KEY || !stages.some((s) => s.stage_key === targetStageKey)) {
      message.warning('Drop on a column to move the deal');
      return;
    }

    let moved = false;
    setDeals((prev) => {
      const deal = prev.find((d) => d.id === dealId);
      if (!deal || deal.stage_key === targetStageKey) return prev;

      moved = true;
      const fromKey = deal.stage_key;
      const fromKnownStage = stages.some((s) => s.stage_key === fromKey);

      setStages((prevStages) => prevStages.map((s) => {
        if (fromKnownStage && s.stage_key === fromKey) {
          return { ...s, deal_count: Math.max(0, (s.deal_count ?? 0) - 1) };
        }
        if (s.stage_key === targetStageKey) {
          return { ...s, deal_count: (s.deal_count ?? 0) + 1 };
        }
        return s;
      }));

      if (!fromKnownStage) {
        setOrphanMeta((meta) => ({
          ...meta,
          count: Math.max(0, (meta.count ?? 0) - 1),
        }));
      }

      return prev.map((d) => (d.id === dealId ? { ...d, stage_key: targetStageKey } : d));
    });

    if (!moved) return;

    try {
      await moveDeal(dealId, targetStageKey);
      await loadBoard({ showSpinner: false });
    } catch (err) {
      message.error(getHttpErrorMessage(err));
      await loadBoard({ showSpinner: false });
    }
  }, [hasCrmAccess, loadBoard, message, stages]);

  const handleDeleteDeal = useCallback((deal) => {
    if (!hasCrmAccess || !deal) return;
    modal.confirm({
      title: 'Delete this deal?',
      content: 'This cannot be undone. Stage history for this deal will be removed.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeletingDealId(deal.id);
        try {
          await deleteDeal(deal.id);
          message.success('Deal deleted');
          if (dealDrawer.deal?.id === deal.id) {
            setDealDrawer({ open: false, deal: null, stageKey: null });
          }
          await loadBoard({ showSpinner: false });
        } catch (err) {
          message.error(getHttpErrorMessage(err));
        } finally {
          setDeletingDealId(null);
        }
      },
    });
  }, [dealDrawer.deal?.id, hasCrmAccess, loadBoard, message, modal]);

  const handleDragEnd = async ({ active, over }) => {
    setActiveDeal(null);
    if (!over || !hasCrmAccess) return;
    const dealId = Number(String(active.id).replace('deal-', ''));
    const stageKeys = stages.map((s) => s.stage_key);
    const targetStageKey = resolveDropStageKey(over, deals, stageKeys);
    if (!targetStageKey) {
      message.warning('Drop on a column to move the deal');
      return;
    }
    await handleMoveDeal(dealId, targetStageKey);
  };

  const firstStageKey = stages[0]?.stage_key;

  return (
    <Layout
      overflowX="hidden"
      header={(
        <Layout.Header
          title="Pipeline"
          description="Manage catering sales opportunities across your pipeline stages."
          extra={(
            <Space wrap>
              {canEditStages && (
                <Button icon={<SettingOutlined />} onClick={() => setStagesDrawerOpen(true)}>
                  Edit stages
                </Button>
              )}
              {hasCrmAccess && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setDealDrawer({ open: true, deal: null, stageKey: firstStageKey })}
                >
                  New deal
                </Button>
              )}
            </Space>
          )}
        />
      )}
    >
      {loading ? (
        <Spin />
      ) : stages.length === 0 ? (
        <Empty description="No pipeline stages" />
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <PipelineBoardScroll stageCount={boardStages.length} freezeScrollChrome={stagesDrawerOpen}>
            {boardStages.map((stage) => (
              <StageColumn
                key={stage.stage_key}
                stage={stage}
                stages={stages}
                deals={
                  stage.stage_key === UNASSIGNED_STAGE_KEY
                    ? unassignedDeals
                    : (dealsByStage[stage.stage_key] ?? [])
                }
                canDrag={hasCrmAccess && stage.stage_key !== UNASSIGNED_STAGE_KEY}
                canMoveStage={hasCrmAccess}
                onEditDeal={(deal) => setDealDrawer({ open: true, deal, stageKey: deal.stage_key })}
                onDeleteDeal={handleDeleteDeal}
                onMoveStage={handleMoveDeal}
                deletingDealId={deletingDealId}
              />
            ))}
          </PipelineBoardScroll>
          <DragOverlay>
            {activeDeal ? <DealCardContent deal={activeDeal} onClick={() => {}} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      <EditStagesDrawer
        open={stagesDrawerOpen}
        stages={stages}
        onClose={() => setStagesDrawerOpen(false)}
        onSaved={loadBoard}
      />
      <DealFormDrawer
        open={dealDrawer.open}
        deal={dealDrawer.deal}
        stages={stages}
        defaultStageKey={dealDrawer.stageKey}
        onClose={() => setDealDrawer({ open: false, deal: null, stageKey: null })}
        onSaved={loadBoard}
      />
    </Layout>
  );
}

export default withGuard(Pipelines, [PERMISSIONS.CUSTOMERS], { redirect: true });
